import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import * as Sentry from '@sentry/react'
import './styles/global.css'
import App from './App.tsx'
import styles from './App.module.css'

declare global {
  interface Window {
    // Set by events/index.cfm alongside __GOOGLE_MAPS_KEY__ - environment mirrors
    // application.environment ("dev"/"test"/"prod"), release is the build-mtime assetVersion
    // string already used to cache-bust eventslist.js/css, same convention as event-page's
    // main.tsx.
    __RCS_ENV__?: string
    __RCS_RELEASE__?: string
    // Set by events/index.cfm only inside its GPT bootstrap block (non-localhost, ads not
    // disabled for the viewer's account) - tells SponsorAds.tsx real ad slots were defined
    // server-side and it's safe to call googletag.display() on them.
    __GOOGLE_ADS_ENABLED__?: boolean
    googletag?: { cmd: Array<() => void>; display: (id: string) => void }
    // Set by events/index.cfm from the Node region-pages generator's own regions.json manifest
    // (Feature 164) - only regions that actually have a published /rc-races/{slug}/ page.
    // Feeds BrowseByStateStrip.tsx, which needs no separate fetch because of this.
    __RC_RACES_REGIONS__?: Array<{ slug: string; name: string; country: 'US' | 'CA' }>
    // Set by events/index.cfm from _eventsListGate.cfm's eventsListResult - the exact raw (pre-
    // camelize) getEventsListData() struct. Lets the first render show real data immediately
    // instead of "Loading…", and skips the redundant duplicate fetch - see
    // data/eventsListApi.ts's getSeededEventsListData().
    __RCS_EVENTS_LIST_DATA__?: unknown
  }
}

// DSN is a public, write-only identifier, same project (RogueAutomations/RCSignup) as
// event-page's main.tsx - distinguished by the `component` tag set on every captured exception
// throughout this app's data/hooks files, not by a separate Sentry project.
Sentry.init({
  dsn: 'https://06500ea457fae057a84baa7767eb04f4@o4511344118398976.ingest.us.sentry.io/4512189328064512',
  environment: window.__RCS_ENV__ || 'unknown',
  release: window.__RCS_RELEASE__ ? `events-list@${window.__RCS_RELEASE__}` : undefined,
})

function ErrorFallback() {
  return (
    <div className={styles.stateScreen}>
      <p>Something went wrong loading this page.</p>
      <a href="/events/index.cfm">Reload</a>
    </div>
  )
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <Sentry.ErrorBoundary fallback={<ErrorFallback />}>
      <App />
    </Sentry.ErrorBoundary>
  </StrictMode>,
)
